/**
 * src/i18n/error_codes.js
 *
 * ─── WHY THIS EXISTS ────────────────────────────────────────────────────────
 * Cloud Functions deliberately return OPAQUE, machine-readable error codes.
 * `internalError(tag, err)` (functions/src/common/internal_error.ts) returns
 * `new HttpsError(code, tag)` where the MESSAGE *IS* THE TAG — e.g.
 * `NOTIFICATIONS_READ_FAILED`. That opacity is correct and intentional: it
 * stops internal detail (collection paths, index URLs, service-account
 * identities, stack fragments) leaking to an arbitrary authenticated caller.
 * The `httpserror-leak.mjs` gate exists to keep it that way.
 *
 * But nothing on the client turned a code back into a sentence. ~50 screens
 * did this:
 *
 *     `${t('alert_rules.saveRuleFailedPrefix')} ${String(err && err.message)}`
 *
 * …so an Arabic user read a localized prefix followed by a raw SCREAMING_SNAKE
 * English identifier:
 *
 *     تعذّر حفظ القاعدة — NOTIFICATIONS_READ_FAILED
 *
 * That is an Arabic-first failure (the product's stated moat) and plain bad UX
 * in English too. A code is not an error message. This module is the missing
 * half: code in, human sentence out, in the right language.
 *
 * ─── THE DESIGN: RULES, NOT A 369-ENTRY TABLE ───────────────────────────────
 * Measured on the live tree (2026-08-14): 307 real `internalError` tags (plus
 * 3 test fixtures — SOME_TAG / T / WRAPPER_TAG — that never reach a client),
 * and 11 distinct literal HttpsError codes. The tags are HIGHLY PATTERNED:
 *
 *     _READ_FAILED / _FETCH|LOAD|LIST|QUERY_FAILED   114   -> family "read"
 *     _UNEXPECTED                                    116   -> family "unexpected"
 *     _FAILED (generic tail)                          37   -> family "failed"
 *     _CHECK_FAILED                                   36   -> family "check"
 *     _WRITE|SAVE|UPDATE|PERSIST_FAILED                2   -> family "write"
 *     _DENIED                                          0*  -> family "denied"
 *                                                    ───
 *                                        305 / 310  = 98.4%
 *
 * (*_DENIED appears inside authored permission messages rather than as an
 * internalError tag tail, but the rule is kept — it costs one regex and the
 * family is real in the HttpsError vocabulary.)
 *
 * So SIX suffix rules classify 98.4% of the tag corpus. The remaining 5 are
 * two real one-offs (`..._FAILED_RETRY`, `..._GUARD_TRIPPED` — both handled by
 * explicit tail rules below) and the 3 test fixtures.
 *
 * A SECOND, independent axis gives specificity: the tag's leading DOMAIN token
 * (PAYROLL, LEAVE, ATTENDANCE, …). 121 distinct tokens exist; the ~70 mapped
 * below cover every token that appears more than once. An UNMAPPED domain is
 * not a failure — it degrades to the subject-less family sentence, which is
 * still a complete, dignified, correctly-localized sentence. That is the whole
 * reason this approach beats a hand-written table: the fallback is *good*, so
 * coverage gaps are invisible to the user rather than embarrassing.
 *
 * ─── WHY TWO OUTPUT SHAPES ──────────────────────────────────────────────────
 * Measured across the 216 client call sites of the per-screen error helpers:
 * 48 already sit behind a localized context prefix that ends in an em-dash
 * ("تعذّر حفظ القاعدة —" / "Couldn't save the rule —"), and 168 are STANDALONE
 * (`setModalMsg(overlay, '#cb-msg', errMessage(err), true)`). Those want
 * different text, so there are two entry points:
 *
 *   resolveErrorMessage(err, lang)  standalone — names the subject when known
 *   resolveErrorReason(err, lang)   after a prefix — subject-less, no repetition
 *
 * `resolveErrorReason` is just the subject-less variant of the same sentence,
 * so there is ONE key set, not two.
 *
 * ─── CLASSIFICATION: FOUR KINDS OF `err.message`, NOT TWO ───────────────────
 * The naive split is "opaque tag" vs "authored prose, safe to show". That is
 * WRONG on this codebase, and measuring it is what shaped this module: of 238
 * single-quoted `throw new Error('…')` sites in web-admin/src/screens, 222 are
 * DEVELOPER DIAGNOSTICS — overwhelmingly `'getAlertRules: unexpected response
 * shape'` (295 occurrences). Showing that to a user is exactly as bad as
 * showing NOTIFICATIONS_READ_FAILED, and a two-way split would have shipped it
 * verbatim. Hence four kinds:
 *
 *   TAG        SCREAMING_SNAKE, no spaces        -> localize by family+subject
 *   DEV        'ident: …', 'unexpected response  -> localize by code; never shown
 *              shape', bare lowercase fragments,
 *              stack frames, Error-name prefixes
 *   AUTHORED   a real sentence the SERVER wrote  -> pass through (see caveat)
 *              ("Employee code must not be
 *              empty.", "No fields supplied to
 *              update.")
 *   EMPTY      nothing usable                    -> localize by code
 *
 * AUTHORED pass-through caveat, deliberate and documented: those server strings
 * are English-only and there are ~1400 `invalid-argument` sites authoring them.
 * They are ALSO the only place the specific bad field is named, so suppressing
 * them in favour of a generic localized line would destroy the one actionable
 * detail the user has. The lesser evil is to show them. Localizing that corpus
 * is a functions/src change and belongs to a different lane.
 *
 * PERMISSION DENIALS ARE THE EXCEPTION and are localized even when authored:
 * "Admin or HR role required." is not something a user can act on themselves,
 * so the localized "you don't have permission / ask your administrator"
 * sentence is strictly better than English prose in an Arabic UI. The one
 * authored permission variant worth distinguishing — a missing MODULE
 * ENTITLEMENT — gets its own sentence, because the remedy differs (contact the
 * administrator to enable the module, not "you lack a role").
 *
 * ─── NEVER THE RAW CODE ─────────────────────────────────────────────────────
 * An unknown/unmapped code returns a dignified generic sentence in the right
 * language. The raw code goes to `console.warn` for support, never to the DOM.
 *
 * ─── ARABIC ─────────────────────────────────────────────────────────────────
 * Reuses this product's shipped vocabulary rather than inventing a register:
 *   تعذّر …                     the established "couldn't …" form (≈120 uses)
 *   يُرجى إعادة المحاولة.        the established "please try again"
 *   ليست لديك صلاحية …          the established permission form
 *   غير متاح                    (NOT غير متوفر — ratified glossary, gated)
 *   مسير الرواتب / كشف الراتب    payroll run / payslip canon (قسيمة راتب banned)
 *   المنشأة                     (NOT المستأجر for tenant — ratified, gated)
 * `i18n-glossary.mjs` enforces the banned pairs; `جميع` is preferred over `كل`
 * for "all" (no "all X" strings are introduced here).
 *
 * ─── KEYS LIVE IN en.js / ar.js ─────────────────────────────────────────────
 * Every string is an `errors.*` key in BOTH dictionaries. The keys are built by
 * concatenation at the `tr()` call, which makes them DYNAMIC call sites that
 * `i18n-used-key.mjs` cannot statically verify (it counts and reports them as
 * skipped — by design). That verification gap is closed instead by
 * `client-error-opacity.mjs`, which regex-extracts every `'errors.…'` literal
 * from THIS file and asserts each exists in both dicts.
 */

import { peekDict } from './dicts.js';
import { getLang } from './store.js';

// ── Lookup ──────────────────────────────────────────────────────────────────
// Mirrors t.js's fallback chain (active dict -> en -> raw key) but takes an
// EXPLICIT lang, because callers of this module sometimes need to render a
// message for a language other than the ambient one (and because a resolver
// that silently depends on global state is untestable).

// Dictionaries are lazy (see dicts.js) — resident by the time any UI can call
// in, because main.js awaits the active language before mounting and setLang
// awaits the incoming one before notifying. EMPTY keeps the lookup total if a
// caller ever asks for a language that was never loaded: the chain falls
// through to the raw key, which is the same worst case this resolver always
// had.
const EMPTY = Object.freeze(Object.create(null));

function tr(key, lang, params) {
  const active = peekDict(lang) || peekDict(getLang()) || EMPTY;
  const en = peekDict('en') || EMPTY;
  const raw =
    (Object.prototype.hasOwnProperty.call(active, key) && active[key]) ||
    (Object.prototype.hasOwnProperty.call(en, key) && en[key]) ||
    key;
  if (!params || typeof raw !== 'string') return raw;
  return raw.replace(/\{(\w+)\}/g, (m, name) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : m,
  );
}

// ── Tag families ────────────────────────────────────────────────────────────
// Ordered — first match wins. The generic `_FAILED` tail MUST come last, after
// every compound tail that also ends in _FAILED.

const FAMILY_RULES = [
  [/_(?:READ|FETCH|LOAD|LIST|QUERY)_FAILED$/, 'read'],
  [/_CHECK_FAILED$/, 'check'],
  [/_(?:WRITE|SAVE|UPDATE|PERSIST)_FAILED$/, 'write'],
  [/_UNEXPECTED$/, 'unexpected'],
  [/_DENIED$/, 'denied'],
  [/_GUARD_TRIPPED$/, 'failed'],
  [/_FAILED_RETRY$/, 'failed'],
  [/_FAILED$/, 'failed'],
];

const FAMILIES = ['read', 'check', 'write', 'unexpected', 'failed', 'denied'];

/** Leading noise tokens that are not the subject (GET_X_… -> X). */
const LEADING_NOISE = /^(?:GET|MY|LIST|FETCH|LOAD|ADMIN)_/;

// ── Domain subject vocabulary ───────────────────────────────────────────────
// Covers every domain token appearing more than once in the live tag corpus.
// Verb-ish tokens (APPROVE, MARK, RESOLVE, APPLY, MIGRATE, CLOSE, PROCESS,
// BACKFILL) are DELIBERATELY absent: they are not subjects, and mapping them
// would produce "Couldn't load approve data". They fall through to the
// subject-less sentence, which is correct.

const SUBJECTS = {
  PAYROLL: 'payroll', LEAVE: 'leave', HR: 'hr', ORG: 'org',
  ATTENDANCE: 'attendance', VENDOR: 'vendor', STOCK: 'stock',
  REVENUE: 'revenue', PERF: 'performance', PERFORMANCE: 'performance',
  HOSPITALITY: 'hospitality', WPS: 'wps', TENANT: 'tenant', SALES: 'sales',
  EMPLOYEE: 'employee', CYCLE: 'cycleCount', WORK: 'work',
  PROGRESS: 'progressClaim', MFG: 'manufacturing', JOB: 'job', GL: 'gl',
  CRM: 'crm', AP: 'ap', AR: 'ar', SUCCESSION: 'succession', SETUP: 'setup',
  RFIS: 'rfi', PM: 'preventiveMaintenance', PAYSLIP: 'payslip',
  MUQEEM: 'muqeem', MAINTENANCE: 'maintenance', HOLIDAYS: 'holidays',
  FIXED: 'fixedAssets', CONTRACT: 'contract', CONTRACTS: 'contract',
  BRANDING: 'branding', BRANCH: 'branch', BRANCHES: 'branch', BOMS: 'bom',
  AUDIT: 'audit', WORKSTREAMS: 'workstream', WORKFORCE: 'workforce',
  TEAM: 'team', TASK: 'task', SUPPORT: 'support',
  SUBCONTRACTOR: 'subcontractor', SCHEDULE: 'schedule',
  SAUDIZATION: 'saudization', RFQS: 'rfq', RESERVATION: 'reservation',
  RELEASE: 'release', RECURRING: 'recurring', QUALITY: 'quality',
  PURCHASE: 'purchaseOrder', PULSE: 'pulse', PRODUCTION: 'production',
  POLICIES: 'policy', PEER: 'peerReview', NOTIFICATIONS: 'notifications',
  MATERIALS: 'materials', LEASES: 'lease', LANDLORD: 'landlord',
  KNOWLEDGE: 'knowledgeBase', KB: 'knowledgeBase', JOURNEYS: 'journey',
  INSIGHTS: 'insights', INCIDENT: 'incident', INCIDENTS: 'incident',
  HOUSEKEEPING: 'housekeeping', GRIEVANCES: 'grievance', GOSI: 'gosi',
  FX: 'fx', FEEDBACK: 'feedback', EXPENSE: 'expense', EXECUTIVE: 'executive',
  DOCUMENT: 'document', DELEGATIONS: 'delegation', DASHBOARD: 'dashboard',
  CUSTOMER: 'customer', COST: 'cost', CONSTRUCTION: 'construction',
  COMPANY: 'company', COMMISSIONS: 'commission', CHATS: 'chat',
  CHANGE: 'changeOrder', CASH: 'cash', CANDIDATES: 'candidate',
  APPROVAL: 'approval', ANNOUNCEMENTS: 'announcement', ANALYTICS: 'analytics',
  ALERT: 'alertRule', PROCUREMENT: 'procurement', REPORTS: 'report',
  POS: 'pos', PMS: 'pms', PLATFORM: 'platform', PERIOD: 'period',
  OFFER: 'offer', LABOR: 'labor', ROLE: 'role', SESSION: 'session',
  DEPARTMENT: 'department', BILLING: 'billing', REFUND: 'refund',
};

// ── Firebase / HttpsError code vocabulary ───────────────────────────────────
// These are what a user most often actually hits, and their advice genuinely
// differs — a generic "something went wrong" would be actively unhelpful for
// every one of them.

const CODES = new Set([
  'permission-denied', 'unauthenticated', 'unavailable', 'failed-precondition',
  'resource-exhausted', 'deadline-exceeded', 'not-found', 'already-exists',
  'aborted', 'invalid-argument', 'cancelled', 'unimplemented',
  'out-of-range', 'data-loss', 'internal', 'unknown',
]);

/** Codes whose localized advice ALWAYS beats whatever prose came with them. */
const CODE_WINS_OVER_PROSE = new Set([
  'permission-denied', 'unauthenticated', 'unavailable', 'resource-exhausted',
  'deadline-exceeded', 'cancelled', 'aborted',
]);

// ── Message classification ──────────────────────────────────────────────────

const TAG_RE = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/;

/** Developer diagnostics that must never reach a user. See header. */
const DEV_TEXT_RES = [
  /unexpected response shape/i,
  /^[A-Za-z_$][\w$]*(?:\.[\w$]+)*\s*[:—-]\s/, // "getAlertRules: …"
  /^\w*(?:Error|Exception)\b\s*:/,                  // "TypeError: …"
  /\bat\s+[\w$.]+\s*\(/,                            // stack frame
  /\b(?:undefined|null|NaN)\b/,
  /^[a-z][a-z0-9]*(?:[\s-][a-z0-9]+){0,5}$/,        // "missing tenant id"
  /^\s*\{|\bJSON\b|\bobject Object\b/,
];

/** A genuine, user-grade sentence a server author wrote. */
function looksAuthored(msg) {
  if (msg.length < 12 || msg.length > 400) return false;
  if (!/\s/.test(msg)) return false;
  if (DEV_TEXT_RES.some((re) => re.test(msg))) return false;
  return /^[A-Z؀-ۿ]/.test(msg) && /[.!?؟]$/.test(msg);
}

const ENTITLEMENT_RE = /\bentitlement\b|\bmodule\b/i;

/**
 * Normalizes whatever the caller caught into `{ code, message }`.
 * Accepts an Error/HttpsError, a plain `{ code, message }` envelope, or a
 * bare string (the `res.error` shape some callables return).
 */
function unwrap(err) {
  if (err == null) return { code: '', message: '' };
  if (typeof err === 'string') return { code: '', message: err.trim() };
  const rawCode = err.code != null ? String(err.code) : '';
  // The callable SDK prefixes codes: 'functions/permission-denied'.
  const code = rawCode.includes('/') ? rawCode.slice(rawCode.lastIndexOf('/') + 1) : rawCode;
  const message = err.message != null ? String(err.message).trim() : '';
  return { code, message };
}

/**
 * Classifies an error without rendering it. Exported for tests and for the
 * `client-error-opacity.mjs` gate's self-check.
 *
 * @returns {{kind:'tag'|'dev'|'authored'|'empty', code:string, message:string,
 *            family:string|null, subject:string|null}}
 */
export function classifyError(err) {
  const { code, message } = unwrap(err);
  let kind = 'empty';
  if (message && TAG_RE.test(message)) kind = 'tag';
  else if (message && looksAuthored(message)) kind = 'authored';
  else if (message) kind = 'dev';

  let family = null;
  let subject = null;
  if (kind === 'tag') {
    for (const [re, fam] of FAMILY_RULES) {
      if (re.test(message)) { family = fam; break; }
    }
    const stem = message
      .replace(/_(?:READ|FETCH|LOAD|LIST|QUERY|CHECK|WRITE|SAVE|UPDATE|PERSIST)_FAILED$/, '')
      .replace(/_UNEXPECTED$|_DENIED$|_GUARD_TRIPPED$|_FAILED_RETRY$|_FAILED$/, '')
      .replace(LEADING_NOISE, '');
    const head = stem.split('_')[0];
    subject = Object.prototype.hasOwnProperty.call(SUBJECTS, head) ? SUBJECTS[head] : null;
  }
  return { kind, code, message, family, subject };
}

// ── Rendering ───────────────────────────────────────────────────────────────

function codeSentence(code, lang) {
  if (code && CODES.has(code)) return tr(`errors.code.${code}`, lang);
  return tr('errors.code.unknown', lang);
}

function render(err, lang, withSubject) {
  const c = classifyError(err);

  // Support keeps the raw code; the DOM never does.
  if (c.kind === 'tag' || c.kind === 'dev') {
    try {
      // eslint-disable-next-line no-console
      console.warn('[staffly:error]', c.code || '(no code)', c.message);
    } catch { /* console unavailable — never let logging break rendering */ }
  }

  // Permission/entitlement denials: localized advice always wins, even over
  // authored prose ("Admin or HR role required." is not user-actionable).
  if (c.code === 'permission-denied') {
    if (ENTITLEMENT_RE.test(c.message)) return tr('errors.code.entitlement', lang);
    return tr('errors.code.permission-denied', lang);
  }
  if (c.code && CODE_WINS_OVER_PROSE.has(c.code)) return codeSentence(c.code, lang);

  // A genuine server-authored sentence is the most actionable thing available.
  if (c.kind === 'authored') return c.message;

  if (c.kind === 'tag' && c.family) {
    if (withSubject && c.subject) {
      return tr(`errors.withSubject.${c.family}`, lang, {
        subject: tr(`errors.subject.${c.subject}`, lang),
      });
    }
    return tr(`errors.reason.${c.family}`, lang);
  }

  return codeSentence(c.code, lang);
}

/**
 * A complete, standalone, localized sentence for `err` — names the subject
 * when the tag's domain is recognized. Use this where the message stands on
 * its own (a modal message line, an inline form error, a toast).
 *
 * @param {unknown} err   caught value: Error/HttpsError, {code,message}, or string
 * @param {'en'|'ar'} [lang] defaults to the ambient UI language
 * @returns {string} never a raw code, never developer text
 */
export function resolveErrorMessage(err, lang) {
  return render(err, lang, true);
}

/**
 * The subject-less variant, for sites that ALREADY carry a localized context
 * prefix ending in an em-dash — e.g.
 *
 *     `${t('alert_rules.saveRuleFailedPrefix')} ${resolveErrorReason(err)}`
 *     -> "تعذّر حفظ القاعدة — تعذّر تحميل البيانات. يُرجى إعادة المحاولة."
 *
 * Naming the subject again there would just repeat the prefix.
 */
export function resolveErrorReason(err, lang) {
  return render(err, lang, false);
}

/** Introspection for gates/tests — the full key space this module can emit. */
export const ERROR_KEY_SPACE = Object.freeze({
  families: FAMILIES,
  subjects: Object.freeze([...new Set(Object.values(SUBJECTS))]),
  codes: Object.freeze([...CODES]),
});
